/**
 * Hero slider — auto-rotates between `[data-hero-slide]` panels, with
 * keyboard-friendly prev / next / dot controls and a pause-on-interact
 * behavior. A horizontal swipe on a touch screen goes to the next or
 * previous slide. Respects `prefers-reduced-motion` (no autoplay) and pauses
 * while the document is hidden.
 *
 * Markup contract:
 *   [data-hero-slider]
 *     [data-autoplay="true|false"]     — section setting; "false" disables autoplay
 *     [data-autoplay-speed="<ms>"]     — section setting; interval between slides
 *     [data-hero-slide]                — repeated, first has `is-active`
 *     [data-hero-prev]                 — previous-slide button
 *     [data-hero-next]                 — next-slide button
 *     [data-hero-dot="<index>"]        — repeated, first has `is-active`
 */
(() => {
  const AUTOPLAY_MS_DEFAULT = 6000;
  const AUTOPLAY_MS_MIN = 1500; // floor — prevents misconfig from spinning the timer too aggressively
  const SWIPE_PX = 40;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (root) => {
    const slides = root.querySelectorAll('[data-hero-slide]');
    const dots = root.querySelectorAll('[data-hero-dot]');
    const prev = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const total = slides.length;
    if (total < 2) return;

    // Read per-section autoplay config from data attributes (set by hero.liquid
    // from the section settings). Defaults keep existing behaviour for sliders
    // rendered without the attributes.
    const autoplayEnabled = root.dataset.autoplay !== 'false';
    const parsedSpeed = parseInt(root.dataset.autoplaySpeed, 10);
    const autoplayMs = Number.isFinite(parsedSpeed) && parsedSpeed >= AUTOPLAY_MS_MIN
      ? parsedSpeed
      : AUTOPLAY_MS_DEFAULT;

    let current = 0;
    let timer = null;

    const go = (index) => {
      current = (index + total) % total;
      slides.forEach((slide, i) => {
        const active = i === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      if (!autoplayEnabled) return;
      if (prefersReducedMotion.matches) return;
      if (document.hidden) return;
      stop();
      timer = window.setInterval(() => go(current + 1), autoplayMs);
    };

    prev?.addEventListener('click', () => {
      go(current - 1);
      start();
    });
    next?.addEventListener('click', () => {
      go(current + 1);
      start();
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        go(Number(dot.dataset.heroDot));
        start();
      });
    });

    // Swipe: a mostly horizontal move of at least SWIPE_PX changes slide.
    let touchX = null;
    let touchY = null;
    root.addEventListener('touchstart', (e) => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
      stop();
    }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      touchX = null;
      if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy)) go(current + (dx < 0 ? 1 : -1));
      start();
    }, { passive: true });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });
    prefersReducedMotion.addEventListener('change', () => {
      if (prefersReducedMotion.matches) stop();
      else start();
    });

    start();
  };

  const ready = () => document.querySelectorAll('[data-hero-slider]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
